/**
 * Service de gestion de la synchronisation offline-first
 * Traite la queue de synchronisation quand la connexion est rétablie
 */

import { db } from '../lib/database';
import { supabase } from '../lib/supabase';
import type { SyncOperation } from '../types';
import { SYNC_PRIORITY } from '../types';
import { readSyncQueueState } from './accountService';

/**
 * Marque une erreur comme DÉFINITIVE : la réessayer ne changera rien, elle ne
 * doit pas consommer trois tentatives pour rien.
 */
const PERMANENT_FAILURE_FLAG = '__permanentSyncFailure';

function markPermanent<T>(error: T): T {
  try {
    (error as any)[PERMANENT_FAILURE_FLAG] = true;
  } catch {
    /* erreur non extensible : on retombe simplement sur le rejeu normal */
  }
  return error;
}

function isPermanentFailure(error: any): boolean {
  return !!error && error[PERMANENT_FAILURE_FLAG] === true;
}

/**
 * Codes d'erreur serveur définitifs pour un mouvement de solde :
 * 42501 = compte d'un autre utilisateur, P0002 = compte inexistant.
 */
function isPermanentMovementError(error: any): boolean {
  const code = error?.code;
  if (code === '42501' || code === 'P0002') return true;
  const message = String(error?.message || '');
  return /forbidden|not found/i.test(message);
}

/**
 * Compteur des soldes absolus HÉRITÉS retirés de la file au rejeu.
 * Exposé pour diagnostic (console) : `bazarkelyLegacyBalanceDrops()`.
 */
let legacyAbsoluteBalanceDropCount = 0;

export function getLegacyAbsoluteBalanceDropCount(): number {
  return legacyAbsoluteBalanceDropCount;
}

if (typeof window !== 'undefined') {
  (window as any).bazarkelyLegacyBalanceDrops = getLegacyAbsoluteBalanceDropCount;
}

/**
 * Convertit une chaîne camelCase en snake_case
 * @param str - Chaîne en camelCase (ex: "linkedGoalId")
 * @returns Chaîne en snake_case (ex: "linked_goal_id")
 */
const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

/**
 * Convertit toutes les clés d'un objet de camelCase vers snake_case
 * Crée une nouvelle copie de l'objet sans modifier l'original
 * @param obj - Objet avec clés en camelCase
 * @returns Nouvel objet avec clés en snake_case
 */
const convertKeysToSnakeCase = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeKey = camelToSnake(key);
      result[snakeKey] = obj[key];
    }
  }
  return result;
};

/**
 * Nombre maximum de tentatives par opération
 */
const MAX_RETRIES = 3;

/**
 * Set pour suivre les opérations en cours de traitement
 * Évite de traiter la même opération deux fois simultanément
 */
const processingOperations = new Set<string>();

/**
 * Flag pour indiquer si le SyncManager est initialisé
 */
let isInitialized = false;

/**
 * Flag pour éviter les syncs simultanées (Background Sync + polling)
 */
let isSyncInProgress = false;

/**
 * Tag pour Background Sync API
 */
const BACKGROUND_SYNC_TAG = 'bazarkely-sync';

/**
 * Configuration du polling intelligent (fallback Safari/Firefox)
 */
let pollingIntervalId: NodeJS.Timeout | null = null;
let consecutiveFailures = 0;
const BASE_POLLING_INTERVAL = 30000; // 30 secondes quand queue a des items
const IDLE_POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutes quand queue vide
const MAX_BACKOFF_INTERVAL = 5 * 60 * 1000; // 5 minutes max backoff
const BACKOFF_MULTIPLIER = 2;

/**
 * Attend qu'une session Supabase valide soit disponible
 * @param maxWaitMs - Temps maximum d'attente en millisecondes (défaut: 10000ms)
 * @returns true si une session valide est détectée, false en cas de timeout
 */
const waitForSession = async (maxWaitMs: number = 10000): Promise<boolean> => {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user) {
      console.log('🔄 [SyncManager] ✅ Session valide détectée');
      return true;
    }

    await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
  }

  console.log('🔄 [SyncManager] ⚠️ Timeout: pas de session après', maxWaitMs, 'ms');
  return false;
};

/**
 * Vérifie si Background Sync API est supporté
 * @returns true si Background Sync est supporté, false sinon
 */
export async function isBackgroundSyncSupported(): Promise<boolean> {
  // Vérifier le support de Service Worker
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Vérifier si l'API sync est disponible
    if (!('sync' in registration)) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Initialise le fallback de synchronisation (polling intelligent)
 * Utilisé uniquement si Background Sync n'est pas supporté (Safari/Firefox)
 */
function initializeSyncFallback(): void {
  // Ne pas initialiser si Background Sync est supporté
  isBackgroundSyncSupported().then(supported => {
    if (supported) {
      console.log('🔄 [SyncManager] ✅ Background Sync supporté, polling désactivé');
      return;
    }

    console.log('🔄 [SyncManager] 🦁 Background Sync non supporté, activation du polling intelligent...');
    startIntelligentPolling();
  });
}

/**
 * Démarre le polling intelligent avec intervalles adaptatifs
 */
function startIntelligentPolling(): void {
  // Arrêter le polling existant si présent
  if (pollingIntervalId) {
    clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  }

  // Fonction de polling adaptatif
  const poll = async () => {
    // Ne pas poller si déjà en sync ou hors ligne
    if (isSyncInProgress || !navigator.onLine) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        return; // Pas de session, skip
      }

      // PWA Phase 3: Compter uniquement les opérations non expirées
      const allPending = await db.syncQueue
        .where('status')
        .anyOf(['pending', 'failed'])
        .filter(op => op.retryCount < MAX_RETRIES)
        .toArray();
      
      const now = new Date();
      const pendingCount = allPending.filter(op => {
        if (op.expiresAt) {
          const expiresAt = op.expiresAt instanceof Date ? op.expiresAt : new Date(op.expiresAt);
          return expiresAt >= now;
        }
        return true;
      }).length;

      if (pendingCount > 0) {
        console.log(`🔄 [SyncManager] 🦁 Polling: ${pendingCount} opération(s) en attente`);
        const successCount = await processSyncQueue(true);
        
        // Réinitialiser les échecs consécutifs en cas de succès
        if (successCount > 0) {
          consecutiveFailures = 0;
        } else {
          consecutiveFailures++;
        }
      } else {
        // Queue vide, réinitialiser les échecs
        consecutiveFailures = 0;
      }
    } catch (error) {
      console.error('🔄 [SyncManager] ❌ Erreur lors du polling:', error);
      consecutiveFailures++;
    }
  };

  // Calculer l'intervalle adaptatif
  const calculateInterval = async (): Promise<number> => {
    try {
      // PWA Phase 3: Compter uniquement les opérations non expirées
      const allPending = await db.syncQueue
        .where('status')
        .anyOf(['pending', 'failed'])
        .filter(op => op.retryCount < MAX_RETRIES)
        .toArray();
      
      const now = new Date();
      const pendingCount = allPending.filter(op => {
        if (op.expiresAt) {
          const expiresAt = op.expiresAt instanceof Date ? op.expiresAt : new Date(op.expiresAt);
          return expiresAt >= now;
        }
        return true;
      }).length;

      if (pendingCount === 0) {
        // Queue vide : intervalle idle
        return IDLE_POLLING_INTERVAL;
      }

      // Queue avec items : intervalle de base avec backoff exponentiel
      const backoffInterval = Math.min(
        BASE_POLLING_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, consecutiveFailures),
        MAX_BACKOFF_INTERVAL
      );
      return backoffInterval;
    } catch (error) {
      // En cas d'erreur, utiliser l'intervalle de base
      return BASE_POLLING_INTERVAL;
    }
  };

  // Fonction récursive avec intervalle adaptatif
  const scheduleNextPoll = async () => {
    const interval = await calculateInterval();
    
    pollingIntervalId = setTimeout(async () => {
      await poll();
      scheduleNextPoll(); // Planifier le prochain poll
    }, interval);
  };

  // Démarrer le premier poll
  scheduleNextPoll();
  console.log('🔄 [SyncManager] 🦁 Polling intelligent démarré');
}

/**
 * Arrête le polling intelligent
 */
function stopIntelligentPolling(): void {
  if (pollingIntervalId) {
    clearTimeout(pollingIntervalId);
    pollingIntervalId = null;
    console.log('🔄 [SyncManager] 🦁 Polling intelligent arrêté');
  }
}

/**
 * Initialise le SyncManager
 * Ajoute un listener pour l'événement 'online'
 * Appelle processSyncQueue() automatiquement quand la connexion est rétablie
 * Détecte le support Background Sync et active le fallback si nécessaire
 */
export function initSyncManager(): void {
  if (isInitialized) {
    console.log('🔄 [SyncManager] Déjà initialisé');
    return;
  }

  console.log('🔄 [SyncManager] Initialisation...');

  // Écouter l'événement 'online' (fonctionne pour tous les navigateurs)
  window.addEventListener('online', () => {
    console.log('🔄 [SyncManager] 🌐 Connexion rétablie, traitement immédiat de la queue...');
    // Sync immédiate sur reconnexion
    consecutiveFailures = 0; // Réinitialiser les échecs
    processSyncQueue().catch(error => {
      console.error('🔄 [SyncManager] ❌ Erreur lors du traitement automatique de la queue:', error);
    });
  });

  // Écouter l'événement 'offline' pour arrêter le polling
  window.addEventListener('offline', () => {
    console.log('🔄 [SyncManager] 📴 Connexion perdue, pause du polling');
    // Le polling se mettra en pause automatiquement (vérifie navigator.onLine)
  });

  // Traiter la queue au démarrage si online
  if (navigator.onLine) {
    console.log('🔄 [SyncManager] 🌐 En ligne au démarrage, traitement de la queue...');
    processSyncQueue().catch(error => {
      console.error('🔄 [SyncManager] ❌ Erreur lors du traitement initial de la queue:', error);
    });
  }

  // Écouter les changements d'état d'authentification
  supabase.auth.onAuthStateChange((event, session) => {
    console.log('🔄 [SyncManager] 🔐 Auth event:', event, session?.user?.email || 'no user');
    
    // Traiter la queue sur connexion OU restauration de session
    if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') && session?.user) {
      console.log('🔄 [SyncManager] 🔐 Session détectée, traitement de la queue...');
      consecutiveFailures = 0; // Réinitialiser les échecs
      processSyncQueue(true).catch(error => {
        console.error('🔄 [SyncManager] ❌ Erreur lors du traitement de la queue:', error);
      });
    }
  });

  // Initialiser le fallback pour Safari/Firefox (si Background Sync non supporté)
  initializeSyncFallback();

  // PWA Phase 3: Nettoyer les opérations expirées au démarrage
  cleanupExpiredOperations().catch(error => {
    console.warn('🔄 [SyncManager] ⚠️ Erreur lors du nettoyage initial des opérations expirées:', error);
  });

  isInitialized = true;
  console.log('🔄 [SyncManager] ✅ Initialisé');
}

/**
 * Traite toutes les opérations en attente dans la queue
 * @param skipSessionCheck - Si true, skip la vérification de session (utilisateur déjà authentifié)
 * @returns Nombre d'opérations traitées avec succès
 */
export async function processSyncQueue(skipSessionCheck: boolean = false): Promise<number> {
  // Vérifier si une sync est déjà en cours (évite les doublons)
  if (isSyncInProgress) {
    console.log('🔄 [SyncManager] ⏸️ Sync déjà en cours, ignorée');
    return 0;
  }

  // Vérifier la connexion
  if (!navigator.onLine) {
    console.warn('🔄 [SyncManager] ⚠️ Hors ligne, impossible de traiter la queue');
    return 0;
  }

  // Marquer comme en cours
  isSyncInProgress = true;

  try {
    // Attendre une session valide avant de traiter (sauf si skipSessionCheck est true)
    if (!skipSessionCheck) {
      const hasSession = await waitForSession();
      if (!hasSession) {
        console.log('🔄 [SyncManager] ⏸️ Sync reportée: pas de session utilisateur');
        return 0;
      }
    } else {
      console.log('🔄 [SyncManager] ✅ Vérification de session ignorée (utilisateur déjà authentifié)');
    }

    // Récupération et traitement des opérations
    console.log('🔄 [SyncManager] 📋 Récupération des opérations en attente...');
    
    // Récupérer toutes les opérations en attente ou en échec
    const allPendingOperations = await db.syncQueue
      .where('status')
      .anyOf(['pending', 'failed'])
      .filter(op => op.retryCount < MAX_RETRIES)
      .toArray();

    // PWA Phase 3: Filtrer les opérations expirées
    const now = new Date();
    const validOperations = allPendingOperations.filter(op => {
      if (op.expiresAt) {
        const expiresAt = op.expiresAt instanceof Date ? op.expiresAt : new Date(op.expiresAt);
        if (expiresAt < now) {
          console.log(`🔄 [SyncManager] ⏰ Opération ${op.id} expirée, ignorée`);
          return false;
        }
      }
      return true;
    });

    if (validOperations.length === 0) {
      console.log('🔄 [SyncManager] ✅ Aucune opération en attente (après filtrage des expirées)');
      return 0;
    }

    // PWA Phase 3: Trier par priorité (lower number = higher priority) puis par timestamp
    const pendingOperations = validOperations.sort((a, b) => {
      // Priorité par défaut: NORMAL (2) si non spécifiée
      const priorityA = a.priority ?? SYNC_PRIORITY.NORMAL;
      const priorityB = b.priority ?? SYNC_PRIORITY.NORMAL;
      
      // Trier par priorité d'abord (ascendant: 0, 1, 2, 3)
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Si même priorité, trier par timestamp (plus ancien en premier)
      const timestampA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
      const timestampB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
      return timestampA - timestampB;
    });

    console.log(`🔄 [SyncManager] 📦 ${pendingOperations.length} opération(s) à traiter (triées par priorité)`);

    let successCount = 0;
    let errorCount = 0;

    // Traiter chaque opération séquentiellement pour éviter les conflits
    for (const operation of pendingOperations) {
      // Vérifier si l'opération n'est pas déjà en cours de traitement
      if (processingOperations.has(operation.id)) {
        console.log(`🔄 [SyncManager] ⏭️ Opération ${operation.id} déjà en cours, ignorée`);
        continue;
      }

      try {
        const success = await processOperation(operation);
        if (success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`🔄 [SyncManager] ❌ Erreur lors du traitement de l'opération ${operation.id}:`, error);
        errorCount++;
      }
    }

    console.log(`🔄 [SyncManager] ✅ Traitement terminé: ${successCount} succès, ${errorCount} erreurs`);
    return successCount;
  } catch (error) {
    console.error('🔄 [SyncManager] ❌ Erreur lors de la récupération de la queue:', error);
    return 0;
  } finally {
    // Libérer le flag de sync en cours
    isSyncInProgress = false;
  }
}

/**
 * Traite une opération de synchronisation
 * @param operation - L'opération à traiter
 * @returns true si l'opération a réussi, false sinon
 */
async function processOperation(operation: SyncOperation): Promise<boolean> {
  // Marquer l'opération comme en cours de traitement
  processingOperations.add(operation.id);

  try {
    // Marquer l'opération comme "processing"
    await db.syncQueue.update(operation.id, { status: 'processing' });

    console.log(`🔄 [SyncManager] 🔄 Traitement de l'opération ${operation.id} (${operation.operation} sur ${operation.table_name})`);

    let result: { error: any } | null = null;

    // Exécuter l'opération selon le type et la table
    switch (operation.table_name) {
      case 'transactions':
        result = await processTransactionOperation(operation);
        break;
      case 'accounts':
        result = await processAccountOperation(operation);
        break;
      case 'account_balance_movements':
        result = await processBalanceMovementOperation(operation);
        break;
      case 'budgets':
        result = await processBudgetOperation(operation);
        break;
      case 'goals':
        result = await processGoalOperation(operation);
        break;
      case 'fee_configurations':
        result = await processFeeConfigurationOperation(operation);
        break;
      case 'personal_loans':
        result = await processPersonalLoanOperation(operation);
        break;
      case 'loan_repayments':
        result = await processLoanRepaymentOperation(operation);
        break;
      case 'loan_interest_periods':
        result = await processLoanInterestPeriodOperation(operation);
        break;
      case 'pending_receipts':
        result = await processPendingReceiptOperation(operation);
        break;
      case 'reimbursement_requests':
        result = await processReimbursementRequestOperation(operation);
        break;
      case 'family_shared_transactions':
        result = await processFamilySharedTransactionOperation(operation);
        break;
      case 'family_sharing_rules':
        result = await processFamilySharingRuleOperation(operation);
        break;
      case 'family_shared_recurring_transactions':
        result = await processFamilySharedRecurringOperation(operation);
        break;
      case 'family_members':
        result = await processFamilyMemberOperation(operation);
        break;
      case 'transaction_receipts':
        result = await processTransactionReceiptOperation(operation);
        break;
      case 'transaction_items':
        result = await processTransactionItemOperation(operation);
        break;
      case 'transaction_dictations':
        result = await processTransactionDictationOperation(operation);
        break;
      default:
        console.error(`🔄 [SyncManager] ❌ Table non supportée: ${operation.table_name}`);
        await db.syncQueue.update(operation.id, {
          status: 'failed',
          retryCount: operation.retryCount + 1
        });
        return false;
    }

    // Vérifier le résultat
    if (result && result.error) {
      throw result.error;
    }

    // Succès : supprimer l'opération de la queue
    await db.syncQueue.delete(operation.id);
    console.log(`🔄 [SyncManager] ✅ Opération ${operation.id} synchronisée avec succès`);
    return true;

  } catch (error: any) {
    console.error(`🔄 [SyncManager] ❌ Erreur lors de la synchronisation de l'opération ${operation.id}:`, error);

    // Une erreur définitive (compte d'un autre utilisateur, compte inexistant)
    // ne mérite pas trois tentatives : on la classe échouée tout de suite.
    const permanent = isPermanentFailure(error);
    const newRetryCount = permanent ? MAX_RETRIES : operation.retryCount + 1;

    if (permanent) {
      console.error(`🔄 [SyncManager] ⛔ Opération ${operation.id} rejetée définitivement, aucun nouvel essai`);
    }

    if (newRetryCount >= MAX_RETRIES) {
      // Maximum de tentatives atteint, marquer comme échoué définitivement
      await db.syncQueue.update(operation.id, {
        status: 'failed',
        retryCount: newRetryCount
      });
      console.error(`🔄 [SyncManager] ❌ Opération ${operation.id} a échoué après ${MAX_RETRIES} tentatives`);
    } else {
      // Réessayer plus tard
      await db.syncQueue.update(operation.id, {
        status: 'pending',
        retryCount: newRetryCount
      });
      console.log(`🔄 [SyncManager] ⏳ Opération ${operation.id} sera réessayée (tentative ${newRetryCount}/${MAX_RETRIES})`);
    }

    return false;
  } finally {
    // Retirer l'opération du set de traitement
    processingOperations.delete(operation.id);
  }
}

/**
 * Traite une opération sur la table transactions
 */
async function processTransactionOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;

  try {
    switch (opType) {
      case 'CREATE': {
        // Pour CREATE, on enlève l'id du data car Supabase le génère
        // Idempotent : garder l'id client + upsert (évite les doublons sur réessai/timeout)
        const snakeCaseData = convertKeysToSnakeCase(data);
        const { error } = await supabase
          .from('transactions')
          .upsert(snakeCaseData, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        // Convertir camelCase → snake_case pour Supabase
        const snakeCaseData = convertKeysToSnakeCase(updateData);
        const { error } = await supabase
          .from('transactions')
          .update(snakeCaseData)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('transactions')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur la table accounts
 */
async function processAccountOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;

  try {
    switch (opType) {
      case 'CREATE': {
        // Idempotent : garder l'id client + upsert (évite les doublons sur réessai/timeout)
        const snakeCaseData = convertKeysToSnakeCase(data);
        const { error } = await supabase
          .from('accounts')
          .upsert(snakeCaseData, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        // Opérations HÉRITÉES : certaines files contiennent encore des UPDATE
        // portant un solde ABSOLU, produits par les versions antérieures à
        // v3.78.0. Les rejouer écraserait les mouvements des autres appareils —
        // c'est exactement l'écrasement que ce chantier supprime. On retire le
        // champ ; le solde est désormais porté par account_balance_movements.
        if (Object.prototype.hasOwnProperty.call(updateData, 'balance')) {
          delete (updateData as any).balance;
          legacyAbsoluteBalanceDropCount++;
          console.warn(`🧹 [syncManager] legacy absolute balance dropped for account ${id}`);
        }
        if (Object.keys(updateData).length === 0) {
          // Il ne restait que le solde : plus rien à envoyer, opération traitée.
          console.log(`🔄 [SyncManager] ℹ️ UPDATE compte ${id} vidée de son solde hérité, rien à envoyer`);
          return null;
        }
        // Convertir camelCase → snake_case pour Supabase
        const snakeCaseData = convertKeysToSnakeCase(updateData);
        const { error } = await supabase
          .from('accounts')
          .update(snakeCaseData)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('accounts')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Rejeu d'un MOUVEMENT de solde mis en file.
 *
 * L'id d'origine est retransmis tel quel : la fonction serveur
 * `apply_balance_movement` ne l'applique qu'une seule fois, quel que soit le
 * nombre de rejeux (un timeout n'est pas un échec — l'envoi a pu aboutir).
 */
async function processBalanceMovementOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;

  if (opType !== 'CREATE') {
    return { error: markPermanent(new Error(`Opération non supportée sur un mouvement de solde: ${opType}`)) };
  }

  try {
    // La fonction est plus recente que les types generes du projet : cast assume.
    const { data: newBalance, error } = await (supabase.rpc as any)('apply_balance_movement', {
      p_id: data.id,
      p_account_id: data.accountId,
      p_delta: data.delta,
      p_kind: data.kind || 'transaction',
      p_source_transaction_id: data.sourceTransactionId ?? null,
    });

    if (error) {
      // « forbidden » (42501) et « account not found » (P0002) sont définitifs :
      // réessayer ne changera rien. Timeout et réseau, eux, restent rejouables.
      if (isPermanentMovementError(error)) {
        console.error(
          `🔄 [SyncManager] ❌ Mouvement ${data.id} refusé définitivement (${error.code}): ${error.message}`
        );
        return { error: markPermanent(error) };
      }
      return { error };
    }

    // Le serveur fait foi : on réaligne la copie locale sur ce qu'il renvoie,
    // augmenté de ce qui attend encore de monter pour ce compte.
    try {
      const serverBalance = Number(newBalance);
      const account = await db.accounts.get(data.accountId);
      if (account && Number.isFinite(serverBalance)) {
        const { pendingDeltaByAccount } = await readSyncQueueState();
        // Cette opération-ci est encore dans la file au moment du calcul : son
        // delta est déjà compris dans le solde renvoyé, il faut le retrancher.
        const stillPending = (pendingDeltaByAccount.get(data.accountId) ?? 0) - Number(data.delta || 0);
        await db.accounts.put({ ...account, balance: serverBalance + stillPending });
      }
    } catch (localError) {
      console.warn('🔄 [SyncManager] ⚠️ Réalignement local du solde impossible (non bloquant):', localError);
    }

    return null;
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur la table budgets
 */
async function processBudgetOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;

  try {
    switch (opType) {
      case 'CREATE': {
        // Idempotent : garder l'id client + upsert (évite les doublons sur réessai/timeout)
        const snakeCaseData = convertKeysToSnakeCase(data);
        const { error } = await supabase
          .from('budgets')
          .upsert(snakeCaseData, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        // Convertir camelCase → snake_case pour Supabase
        const snakeCaseData = convertKeysToSnakeCase(updateData);
        const { error } = await supabase
          .from('budgets')
          .update(snakeCaseData)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('budgets')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur la table goals
 */
async function processGoalOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;

  try {
    switch (opType) {
      case 'CREATE': {
        // Idempotent : garder l'id client + upsert (évite les doublons sur réessai/timeout)
        const snakeCaseData = convertKeysToSnakeCase(data);
        const { error } = await supabase
          .from('goals')
          .upsert(snakeCaseData, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        // Convertir camelCase → snake_case pour Supabase
        const snakeCaseData = convertKeysToSnakeCase(updateData);
        const { error } = await supabase
          .from('goals')
          .update(snakeCaseData)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('goals')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur la table fee_configurations
 */
async function processFeeConfigurationOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;

  try {
    switch (opType) {
      case 'CREATE': {
        // Idempotent : garder l'id client + upsert (évite les doublons sur réessai/timeout)
        const { error } = await supabase
          .from('fee_configurations')
          .upsert(data, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from('fee_configurations')
          .update(updateData)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('fee_configurations')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur la table personal_loans (module Prêts Familiaux)
 *
 * Note : le data poussé par loanService.ts est déjà en snake_case (via loanToRow()),
 * donc convertKeysToSnakeCase serait un no-op ici. On insère/update tel quel.
 */
async function processPersonalLoanOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        // Cast `as any` requis : personal_loans n'est pas dans le schema TS Supabase généré
        const { error } = await supabase.from('personal_loans').upsert(data as any, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from('personal_loans')
          .update(updateData as any)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase.from('personal_loans').delete().eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur la table loan_repayments (data déjà en snake_case)
 */
async function processLoanRepaymentOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        const { error } = await supabase.from('loan_repayments').upsert(data as any, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from('loan_repayments')
          .update(updateData as any)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase.from('loan_repayments').delete().eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur la table loan_interest_periods (data déjà en snake_case)
 */
async function processLoanInterestPeriodOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        const { error } = await supabase.from('loan_interest_periods').upsert(data as any, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from('loan_interest_periods')
          .update(updateData as any)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase.from('loan_interest_periods').delete().eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur pending_receipts (cas spécial : upload différé vers Supabase Storage)
 *
 * Flux :
 * 1. Récupère le PendingReceipt local (avec File blob) depuis IndexedDB
 * 2. Upload le blob vers le bucket 'loan-receipts'
 * 3. Génère une URL signée 1 an
 * 4. UPDATE le loan_repayments.receipt_url correspondant
 * 5. Supprime le PendingReceipt local
 *
 * Important : seul CREATE est traité ici (l'opération signale "à uploader").
 */
async function processPendingReceiptOperation(operation: SyncOperation): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    if (opType !== 'CREATE') {
      return { error: new Error(`pending_receipts ne supporte que CREATE, reçu ${opType}`) };
    }
    const pendingId = data.id;
    const pending = await db.pendingReceipts.get(pendingId);
    if (!pending) {
      // Déjà traité ou nettoyé — pas d'erreur
      return null;
    }

    const sanitizedName = pending.fileName
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    const filePath = `${pending.userId}/${Date.now()}_${sanitizedName}`;

    const { error: uploadError } = await supabase.storage
      .from('loan-receipts')
      .upload(filePath, pending.fileBlob, { upsert: false });
    if (uploadError) {
      return { error: uploadError };
    }

    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from('loan-receipts')
      .createSignedUrl(filePath, 60 * 60 * 24 * 365);
    if (signedUrlError || !signedUrlData) {
      return { error: signedUrlError || new Error('Erreur création URL signée') };
    }

    // Lier l'URL au loan_repayments si l'ID est renseigné
    if (pending.repaymentId) {
      const { error: updErr } = await supabase
        .from('loan_repayments')
        .update({ receipt_url: signedUrlData.signedUrl } as any)
        .eq('id', pending.repaymentId);
      if (updErr) {
        return { error: updErr };
      }
    }

    // Nettoyage local
    await db.pendingReceipts.delete(pendingId);
    console.log(`🔄 [SyncManager] 📎 Receipt uploadé et lié au repayment ${pending.repaymentId}`);
    return null;
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur la table reimbursement_requests (module Remboursements
 * Familiaux — S69 phase 1). Data déjà en snake_case poussé par reimbursementService.
 */
async function processReimbursementRequestOperation(
  operation: SyncOperation
): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        const { error } = await supabase
          .from('reimbursement_requests')
          .upsert(data as any, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from('reimbursement_requests')
          .update(updateData as any)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('reimbursement_requests')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur la table family_shared_transactions (S72 — module Family
 * Sharing offline-first). Data poussé par familySharingService est déjà en snake_case.
 */
async function processFamilySharedTransactionOperation(
  operation: SyncOperation
): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        const { error } = await supabase
          .from('family_shared_transactions')
          .upsert(data as any, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from('family_shared_transactions')
          .update(updateData as any)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('family_shared_transactions')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur family_sharing_rules (data déjà en snake_case).
 */
async function processFamilySharingRuleOperation(
  operation: SyncOperation
): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        const { error } = await supabase
          .from('family_sharing_rules')
          .upsert(data as any, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from('family_sharing_rules')
          .update(updateData as any)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('family_sharing_rules')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur family_shared_recurring_transactions (data déjà en snake_case).
 */
async function processFamilySharedRecurringOperation(
  operation: SyncOperation
): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        const { error } = await supabase
          .from('family_shared_recurring_transactions')
          .upsert(data as any, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from('family_shared_recurring_transactions')
          .update(updateData as any)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('family_shared_recurring_transactions')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur family_members (data déjà en snake_case).
 *
 * Utilisé principalement par leaveFamilyGroup (soft delete : update is_active=false).
 * CREATE est aussi supporté pour cohérence (joinFamilyGroup reste online-only mais
 * pourrait passer offline-first à l'avenir).
 */
async function processFamilyMemberOperation(
  operation: SyncOperation
): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        const { error } = await supabase
          .from('family_members')
          .upsert(data as any, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from('family_members')
          .update(updateData as any)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await supabase
          .from('family_members')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur transaction_receipts (module Scan de ticket).
 * Data poussé par receiptService est déjà en snake_case + id client.
 * Rejeu idempotent : upsert(onConflict: 'id', ignoreDuplicates: true) — un envoi
 * « expiré-mais-commité » et le rejeu convergent sur la même ligne.
 */
async function processTransactionReceiptOperation(
  operation: SyncOperation
): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        const { error } = await (supabase as any)
          .from('transaction_receipts')
          .upsert(data, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await (supabase as any)
          .from('transaction_receipts')
          .update(updateData)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await (supabase as any)
          .from('transaction_receipts')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur transaction_items (module Scan de ticket).
 * Data déjà en snake_case + id client. Rejeu idempotent comme ci-dessus.
 */
async function processTransactionItemOperation(
  operation: SyncOperation
): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    switch (opType) {
      case 'CREATE': {
        const { error } = await (supabase as any)
          .from('transaction_items')
          .upsert(data, { onConflict: 'id', ignoreDuplicates: true });
        return error ? { error } : null;
      }
      case 'UPDATE': {
        const { id, ...updateData } = data;
        const { error } = await (supabase as any)
          .from('transaction_items')
          .update(updateData)
          .eq('id', id);
        return error ? { error } : null;
      }
      case 'DELETE': {
        const { id } = data;
        const { error } = await (supabase as any)
          .from('transaction_items')
          .delete()
          .eq('id', id);
        return error ? { error } : null;
      }
      default:
        return { error: new Error(`Opération non supportée: ${opType}`) };
    }
  } catch (error) {
    return { error };
  }
}

/**
 * Traite une opération sur transaction_dictations (dictée vocale).
 * Data déjà en snake_case + id client. CREATE seulement : rejeu idempotent,
 * jamais d'écrasement d'une ligne déjà montée.
 */
async function processTransactionDictationOperation(
  operation: SyncOperation
): Promise<{ error: any } | null> {
  const { operation: opType, data } = operation;
  try {
    if (opType !== 'CREATE') {
      return { error: new Error(`Opération non supportée: ${opType}`) };
    }
    const { error } = await (supabase as any)
      .from('transaction_dictations')
      .upsert(data, { onConflict: 'id', ignoreDuplicates: true });
    return error ? { error } : null;
  } catch (error) {
    return { error };
  }
}

/**
 * Déclenche manuellement la synchronisation
 * Utile pour les tests ou les actions utilisateur
 * @returns Nombre d'opérations traitées avec succès
 */
export async function manualSync(): Promise<number> {
  console.log('🔄 [SyncManager] 🔧 Synchronisation manuelle déclenchée');
  return await processSyncQueue();
}

/**
 * Obtient le nombre d'opérations en attente dans la queue (PWA Phase 3: exclut les expirées)
 * @returns Nombre d'opérations en attente
 */
export async function getPendingOperationsCount(): Promise<number> {
  try {
    const allOperations = await db.syncQueue
      .where('status')
      .anyOf(['pending', 'failed'])
      .filter(op => op.retryCount < MAX_RETRIES)
      .toArray();
    
    // PWA Phase 3: Filtrer les opérations expirées
    const now = new Date();
    const validOperations = allOperations.filter(op => {
      if (op.expiresAt) {
        const expiresAt = op.expiresAt instanceof Date ? op.expiresAt : new Date(op.expiresAt);
        return expiresAt >= now;
      }
      return true; // Pas d'expiration, inclure
    });
    
    return validOperations.length;
  } catch (error) {
    console.error('🔄 [SyncManager] ❌ Erreur lors du comptage des opérations:', error);
    return 0;
  }
}

/**
 * Obtient toutes les opérations en attente (PWA Phase 3: exclut les expirées, triées par priorité)
 * Utile pour le debugging ou l'affichage dans l'UI
 * @returns Liste des opérations en attente, triées par priorité puis timestamp
 */
export async function getPendingOperations(): Promise<SyncOperation[]> {
  try {
    const allOperations = await db.syncQueue
      .where('status')
      .anyOf(['pending', 'failed'])
      .filter(op => op.retryCount < MAX_RETRIES)
      .toArray();
    
    // PWA Phase 3: Filtrer les opérations expirées
    const now = new Date();
    const validOperations = allOperations.filter(op => {
      if (op.expiresAt) {
        const expiresAt = op.expiresAt instanceof Date ? op.expiresAt : new Date(op.expiresAt);
        return expiresAt >= now;
      }
      return true; // Pas d'expiration, inclure
    });
    
    // PWA Phase 3: Trier par priorité puis timestamp
    return validOperations.sort((a, b) => {
      const priorityA = a.priority ?? SYNC_PRIORITY.NORMAL;
      const priorityB = b.priority ?? SYNC_PRIORITY.NORMAL;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      const timestampA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
      const timestampB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
      return timestampA - timestampB;
    });
  } catch (error) {
    console.error('🔄 [SyncManager] ❌ Erreur lors de la récupération des opérations:', error);
    return [];
  }
}

/**
 * Nettoie les opérations échouées définitivement (retryCount >= MAX_RETRIES)
 * @returns Nombre d'opérations nettoyées
 */
export async function cleanupFailedOperations(): Promise<number> {
  try {
    const failedOperations = await db.syncQueue
      .where('status')
      .equals('failed')
      .filter(op => op.retryCount >= MAX_RETRIES)
      .toArray();

    const ids = failedOperations.map(op => op.id);
    if (ids.length > 0) {
      await db.syncQueue.bulkDelete(ids);
      console.log(`🔄 [SyncManager] 🗑️ ${ids.length} opération(s) échouée(s) nettoyée(s)`);
    }

    return ids.length;
  } catch (error) {
    console.error('🔄 [SyncManager] ❌ Erreur lors du nettoyage:', error);
    return 0;
  }
}

/**
 * PWA Phase 3: Nettoie les opérations expirées (expiresAt < now)
 * @returns Nombre d'opérations nettoyées
 */
export async function cleanupExpiredOperations(): Promise<number> {
  try {
    const now = new Date();
    const allOperations = await db.syncQueue.toArray();
    
    const expiredOperations = allOperations.filter(op => {
      if (!op.expiresAt) {
        return false; // Pas d'expiration, garder
      }
      const expiresAt = op.expiresAt instanceof Date ? op.expiresAt : new Date(op.expiresAt);
      return expiresAt < now;
    });

    const ids = expiredOperations.map(op => op.id);
    if (ids.length > 0) {
      await db.syncQueue.bulkDelete(ids);
      console.log(`🔄 [SyncManager] ⏰ ${ids.length} opération(s) expirée(s) nettoyée(s)`);
    }

    return ids.length;
  } catch (error) {
    console.error('🔄 [SyncManager] ❌ Erreur lors du nettoyage des opérations expirées:', error);
    return 0;
  }
}

/**
 * PWA Phase 3: Enregistre un tag Background Sync pour déclencher la synchronisation automatique
 * Fonctionne uniquement sur les navigateurs supportant Background Sync API (Chrome/Edge)
 * Fallback silencieux si non supporté (l'événement 'online' prendra le relais)
 * 
 * Cette fonction doit être appelée après avoir ajouté une opération à la queue
 * @param syncTag - Tag personnalisé pour Background Sync (optionnel, défaut: 'bazarkely-sync')
 * @returns true si l'enregistrement a réussi, false sinon
 */
export async function registerBackgroundSync(syncTag?: string): Promise<boolean> {
  // Utiliser le tag fourni ou le tag par défaut
  const tag = syncTag || BACKGROUND_SYNC_TAG;
  
  // Vérifier le support de Service Worker
  if (!('serviceWorker' in navigator)) {
    console.log('🔄 [SyncManager] ⚠️ Service Worker non supporté, Background Sync ignoré');
    return false;
  }

  // Vérifier le support de Background Sync API
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Vérifier si l'API sync est disponible
    if (!('sync' in registration)) {
      console.log('🔄 [SyncManager] ⚠️ Background Sync API non supporté, utilisation du fallback (événement online)');
      return false;
    }

    // Enregistrer le tag de synchronisation
    try {
      await (registration as any).sync.register(tag);
      console.log('🔄 [SyncManager] ✅ Tag Background Sync enregistré:', tag);
      return true;
    } catch (syncError: any) {
      // Erreur possible: tag déjà enregistré (pas grave)
      if (syncError.name === 'InvalidStateError' || syncError.message?.includes('already registered')) {
        console.log('🔄 [SyncManager] ℹ️ Tag Background Sync déjà enregistré:', tag);
        return true;
      }
      console.warn('🔄 [SyncManager] ⚠️ Erreur lors de l\'enregistrement du tag Background Sync:', syncError);
      return false;
    }
  } catch (error) {
    console.warn('🔄 [SyncManager] ⚠️ Erreur lors de l\'accès au Service Worker:', error);
    return false;
  }
}

